import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { AppLayout } from '@/components/layout/AppLayout';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Pill, Droplets, Utensils, Stethoscope, Wrench, Package } from 'lucide-react';
import type { ItemType } from '@/types/inventory';

const unitsByType: Record<ItemType, string[]> = {
  medication: ['comprimidos', 'cápsulas', 'ml', 'ampolas', 'sachês', 'adesivos', 'gotas', 'unidades'],
  hygiene: ['unidades', 'pacotes', 'fraldas', 'pares', 'rolos'],
  nutrition: ['unidades', 'latas', 'sachês', 'ml', 'kg', 'pacotes'],
  medical_supply: ['unidades', 'pacotes', 'pares', 'caixas', 'rolos'],
  equipment: ['unidades', 'litros', 'cilindros'],
  other: ['unidades', 'pacotes', 'caixas']
};

export default function AddInventoryItem() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    item_name: '',
    item_type: 'medication' as ItemType,
    unit: 'comprimidos',
    status: 'ok' as 'ok' | 'low_stock' | 'zero_stock',
    notes: ''
  });

  const itemTypeOptions: { value: ItemType; label: string; icon: React.ReactNode }[] = [
    { value: 'medication', label: t('inventory.itemType.medication'), icon: <Pill className="w-4 h-4" /> },
    { value: 'hygiene', label: t('inventory.itemType.hygiene'), icon: <Droplets className="w-4 h-4" /> },
    { value: 'nutrition', label: t('inventory.itemType.nutrition'), icon: <Utensils className="w-4 h-4" /> },
    { value: 'medical_supply', label: t('inventory.itemType.medical_supply'), icon: <Stethoscope className="w-4 h-4" /> },
    { value: 'equipment', label: t('inventory.itemType.equipment'), icon: <Wrench className="w-4 h-4" /> },
    { value: 'other', label: t('inventory.itemType.other'), icon: <Package className="w-4 h-4" /> }
  ];

  useEffect(() => {
    const defaultUnit = unitsByType[formData.item_type][0];
    setFormData(prev => ({ ...prev, unit: defaultUnit }));
  }, [formData.item_type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.item_name.trim()) {
      toast.error(t('inventory.notifications.nameRequired'));
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('inventory')
        .insert({
          patient_id: patientId,
          item_name: formData.item_name.trim(),
          item_type: formData.item_type,
          unit: formData.unit,
          notes: formData.notes.trim() || null,
          status: formData.status
        });

      if (error) throw error;

      toast.success(t('inventory.notifications.itemAdded'));

      // PostHog Tracking
      captureEvent('inventory_item_added', {
        patient_id: patientId,
        item_name: formData.item_name.trim(),
        item_type: formData.item_type,
        unit: formData.unit
      });

      navigate(`/app/inventory/${patientId}`);

    } catch (error: any) {
      console.error('Error adding item:', error);
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const currentUnits = unitsByType[formData.item_type];

  const getPlaceholder = () => {
    const key = formData.item_type as keyof typeof unitsByType;
    return t(`inventory.placeholder.${key}`) || t('inventory.placeholder.other');
  };

  return (
    <AppLayout
      title={t('inventory.addItem')}
      showBack
      onBack={() => navigate(`/app/inventory/${patientId}`)}
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-4 pb-24">
        {/* Item Type */}
        <Card>
          <CardContent className="pt-4">
            <Label>{t('inventory.form.itemType')} *</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {itemTypeOptions.map(option => (
                <Button
                  key={option.value}
                  type="button"
                  variant={formData.item_type === option.value ? 'default' : 'outline'}
                  className="h-auto py-3 flex-col gap-1"
                  onClick={() => setFormData(prev => ({ ...prev, item_type: option.value }))}
                >
                  {option.icon}
                  <span className="text-xs">{option.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Item Info */}
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div>
              <Label htmlFor="item_name">{t('inventory.form.itemName')} *</Label>
              <Input
                id="item_name"
                value={formData.item_name}
                onChange={e => setFormData(prev => ({ ...prev, item_name: e.target.value }))}
                placeholder={getPlaceholder()}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="unit">{t('inventory.form.unit')}</Label>
              <Select
                value={formData.unit}
                onValueChange={value => setFormData(prev => ({ ...prev, unit: value }))}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currentUnits.map(unit => (
                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">{t('inventory.form.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('inventory.form.notesPlaceholder')}
                className="mt-2"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
          {isLoading ? (
            t('common.saving')
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              {t('inventory.addToInventory')}
            </>
          )}
        </Button>
      </form>
    </AppLayout>
  );
}